import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  AuditPage,
  AuditQuery,
  BootstrapRequest,
  BootstrapResult,
  FillSecretsRequest,
  FillSecretsResult,
  ConfigsState,
  NamespaceConfigs,
  PropagateConfigsRequest,
  PropagateResult,
  PropagateSecretRequest,
  SecretDetail,
  SecretSummary,
  SecretWritePayload,
  SecretsCompareResult,
  SecretsCompareRequest,
} from './types';

/**
 * Public surface of the secrets-configs API. Consumers depend on this
 * interface (via `useApi(secretsConfigsApiRef)`) so they can be tested with a
 * plain mock through `TestApiProvider` without importing the concrete client.
 */
export interface SecretsConfigsApi {
  listSecrets(envId: string): Promise<SecretSummary[]>;
  getSecret(envId: string, secretId: string): Promise<SecretDetail>;
  upsertSecret(
    envId: string,
    secretId: string,
    payload: SecretWritePayload,
  ): Promise<void>;
  createSecret(
    envId: string,
    secretId: string,
    payload: SecretWritePayload,
  ): Promise<void>;
  listNamespaces(envId: string): Promise<string[]>;
  getConfigs(envId: string, namespace: string): Promise<NamespaceConfigs>;
  putConfigs(
    envId: string,
    namespace: string,
    configs: NamespaceConfigs,
  ): Promise<void>;
  getFullConfigsState(envId: string): Promise<ConfigsState>;
  compareSecrets(req: SecretsCompareRequest): Promise<SecretsCompareResult>;
  /** Apply one secret value to several environments at once (DEVPROD-1251). */
  propagateSecret(req: PropagateSecretRequest): Promise<PropagateResult>;
  /** Merge a config-namespace change into several environments (DEVPROD-1251). */
  propagateConfigs(req: PropagateConfigsRequest): Promise<PropagateResult>;
  /** Clone a template environment's config/secret structure into a new one (DEVPROD-1252). */
  bootstrapEnvironment(req: BootstrapRequest): Promise<BootstrapResult>;
  /** Copy operator-selected secret values from a template env into a target, create-missing-only (DEVPROD-1252 D11/D12). */
  fillSecrets(req: FillSecretsRequest): Promise<FillSecretsResult>;
  /** One page of an environment's audit history, newest first (DEVPROD-1139). */
  listAuditEvents(
    envId: string,
    query?: Partial<Omit<AuditQuery, 'envId'>>,
  ): Promise<AuditPage>;
}

export const secretsConfigsApiRef = createApiRef<SecretsConfigsApi>({
  id: 'plugin.secrets-configs.service',
});

export class SecretsConfigsClient implements SecretsConfigsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('secrets-configs');
  }

  /**
   * Resolve the service base URL, issue the fetch, and throw a friendly error
   * on a non-OK response. Every method funnels through here so the base-URL
   * resolution, the ok-check, and the error shape stay identical. `action` is
   * folded into the thrown message ("Failed to <action>: <statusText>").
   */
  private async request(
    path: string,
    action: string,
    init?: RequestInit,
  ): Promise<Response> {
    const url = `${await this.baseUrl()}${path}`;
    // Omit the init arg entirely for plain GETs (rather than passing undefined)
    // so the call shape stays fetch(url) — matches FetchApi's own overloads.
    const resp = init
      ? await this.fetchApi.fetch(url, init)
      : await this.fetchApi.fetch(url);
    if (!resp.ok) throw new Error(`Failed to ${action}: ${resp.statusText}`);
    return resp;
  }

  /** Build a JSON request init (method + Content-Type + serialized body). */
  private static jsonInit(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  private env(envId: string): string {
    return `/environments/${encodeURIComponent(envId)}`;
  }

  // ── Audit ────────────────────────────────────────────────────────────────

  async listAuditEvents(
    envId: string,
    query: Partial<Omit<AuditQuery, 'envId'>> = {},
  ): Promise<AuditPage> {
    const params = new URLSearchParams();
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.actions?.length) params.set('action', query.actions.join(','));
    if (query.actor) params.set('actor', query.actor);
    if (query.results?.length) params.set('result', query.results.join(','));
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);

    const search = params.toString();
    const resp = await this.request(
      `${this.env(envId)}/audit${search ? `?${search}` : ''}`,
      'load audit history',
    );
    return resp.json();
  }

  // ── Secrets ──────────────────────────────────────────────────────────────

  async listSecrets(envId: string): Promise<SecretSummary[]> {
    const resp = await this.request(
      `${this.env(envId)}/secrets`,
      'list secrets',
    );
    return resp.json();
  }

  async getSecret(envId: string, secretId: string): Promise<SecretDetail> {
    const resp = await this.request(
      `${this.env(envId)}/secrets/${encodeURIComponent(secretId)}`,
      'get secret',
    );
    return resp.json();
  }

  async upsertSecret(
    envId: string,
    secretId: string,
    payload: SecretWritePayload,
  ): Promise<void> {
    await this.request(
      `${this.env(envId)}/secrets/${encodeURIComponent(secretId)}`,
      'upsert secret',
      SecretsConfigsClient.jsonInit('PATCH', payload),
    );
  }

  async createSecret(
    envId: string,
    secretId: string,
    payload: SecretWritePayload,
  ): Promise<void> {
    await this.request(
      `${this.env(envId)}/secrets`,
      'create secret',
      SecretsConfigsClient.jsonInit('POST', { name: secretId, ...payload }),
    );
  }

  // ── Configs ──────────────────────────────────────────────────────────────

  async listNamespaces(envId: string): Promise<string[]> {
    const resp = await this.request(
      `${this.env(envId)}/configs/namespaces`,
      'list namespaces',
    );
    return resp.json();
  }

  async getConfigs(
    envId: string,
    namespace: string,
  ): Promise<NamespaceConfigs> {
    const resp = await this.request(
      `${this.env(envId)}/configs/${encodeURIComponent(namespace)}`,
      'get configs',
    );
    return resp.json();
  }

  async putConfigs(
    envId: string,
    namespace: string,
    configs: NamespaceConfigs,
  ): Promise<void> {
    await this.request(
      `${this.env(envId)}/configs/${encodeURIComponent(namespace)}`,
      'save configs',
      SecretsConfigsClient.jsonInit('PUT', configs),
    );
  }

  async getFullConfigsState(envId: string): Promise<ConfigsState> {
    const resp = await this.request(
      `${this.env(envId)}/configs`,
      'get config state',
    );
    return resp.json();
  }

  async compareSecrets(
    req: SecretsCompareRequest,
  ): Promise<SecretsCompareResult> {
    const resp = await this.request(
      `/compare/secrets`,
      'compare secrets',
      SecretsConfigsClient.jsonInit('POST', req),
    );
    return resp.json();
  }

  async propagateSecret(req: PropagateSecretRequest): Promise<PropagateResult> {
    const resp = await this.request(
      `/propagate/secrets`,
      'propagate secret',
      SecretsConfigsClient.jsonInit('POST', req),
    );
    return resp.json();
  }

  async propagateConfigs(
    req: PropagateConfigsRequest,
  ): Promise<PropagateResult> {
    const resp = await this.request(
      `/propagate/configs`,
      'propagate configs',
      SecretsConfigsClient.jsonInit('POST', req),
    );
    return resp.json();
  }

  async bootstrapEnvironment(req: BootstrapRequest): Promise<BootstrapResult> {
    const resp = await this.request(
      `/bootstrap`,
      'bootstrap environment',
      SecretsConfigsClient.jsonInit('POST', req),
    );
    return resp.json();
  }

  async fillSecrets(req: FillSecretsRequest): Promise<FillSecretsResult> {
    const resp = await this.request(
      `/fill/secrets`,
      'fill secrets',
      SecretsConfigsClient.jsonInit('POST', req),
    );
    return resp.json();
  }
}
